import React, { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Loader2, Lock, ShieldCheck, MapPinned, WifiOff, Users, Bluetooth, Star, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Marketing splash for users who haven't paid the £6 Blackout Zone unlock yet.
 *
 * On native (Capacitor app) the unlock CTA opens the system browser to the
 * Stripe Checkout URL — Google Play policy requires the actual purchase to
 * happen outside the WebView. On web, we just window.location to the URL.
 */
const FEATURES = [
  { icon: WifiOff, title: 'A* offline routing', body: 'Routing that keeps working when the signal dies.' },
  { icon: MapPinned, title: 'Cached OSM road graph', body: 'Drive, ride, skate without burning data.' },
  { icon: Users, title: 'Community SOS posts', body: 'Live skater check-ins when phones go dark.' },
  { icon: Bluetooth, title: 'Bluetooth beacon mesh', body: 'Drop offline pings to your crew.' },
  { icon: ShieldCheck, title: 'Saved safe-spots', body: 'Pin shelters, water, hospitals, exits.' },
];

export const BlackoutPaywallSplash = ({ priceLabel = '£6', onUnlocked }) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  const handleUnlock = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in first');
        setLoading(false);
        return;
      }
      const origin = window.location.origin;
      const { data } = await axios.post(
        `${API}/blackout/checkout`,
        {
          success_url: `${origin}/blackout/unlocked`,
          cancel_url: `${origin}/blackout`,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.already_unlocked) {
        toast.success('Already unlocked — refreshing…');
        onUnlocked?.();
        return;
      }

      if (!data.checkout_url) {
        toast.error('Could not open checkout. Try again.');
        return;
      }

      // On the native app, route the user to the system browser per Google
      // Play policy. On web we navigate the same tab.
      if (Capacitor.isNativePlatform()) {
        try {
          const { Browser } = await import('@capacitor/browser');
          await Browser.open({ url: data.checkout_url });
        } catch {
          // Fallback: trust that window.open / .location works in the WebView.
          window.location.href = data.checkout_url;
        }
      } else {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Checkout failed — try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-[calc(100vh-64px)] px-5 py-10 flex items-start justify-center"
      data-testid="blackout-paywall-splash"
    >
      <div className="max-w-lg w-full space-y-6">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="text-muted-foreground hover:text-foreground -ml-2"
          data-testid="blackout-paywall-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>

        <div className="rounded-3xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-pink-500/5 to-transparent p-8 backdrop-blur-sm">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/20 flex items-center justify-center">
              <Lock className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-amber-400 font-bold">Lifetime unlock</p>
              <h1 className="text-2xl font-black leading-tight">Blackout Zone</h1>
            </div>
          </div>

          <p className="text-base text-foreground/90 leading-relaxed mb-6">
            When the network drops, RollM8s keeps rolling. Offline routing, community
            SOS, beacon mesh — built for skaters and drivers who go where signal
            doesn't.
          </p>

          <ul className="space-y-3 mb-6">
            {FEATURES.map((f) => (
              <li key={f.title} className="flex items-start gap-3 text-sm">
                <f.icon className="w-4 h-4 mt-0.5 text-amber-400 shrink-0" />
                <div>
                  <span className="font-semibold">{f.title}</span>
                  <span className="text-muted-foreground"> — {f.body}</span>
                </div>
              </li>
            ))}
          </ul>

          <div className="flex items-baseline gap-2 mb-4">
            <span className="text-4xl font-black text-amber-400">{priceLabel}</span>
            <span className="text-sm text-muted-foreground">one-time · lifetime · this account</span>
          </div>

          <Button
            type="button"
            size="lg"
            onClick={handleUnlock}
            disabled={loading}
            className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold text-base h-12"
            data-testid="blackout-unlock-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>
              <Star className="w-4 h-4 mr-2" /> Unlock for {priceLabel}
            </>}
          </Button>

          <p className="text-[11px] text-muted-foreground mt-3 text-center">
            Secure checkout via Stripe. {Capacitor.isNativePlatform() ? 'Opens browser per Google Play guidelines.' : ''}
          </p>
        </div>

        <p className="text-xs text-muted-foreground text-center px-4">
          Once unlocked, Blackout Zone runs natively on this app on every device you log in to.
          No subscriptions, no renewals.
        </p>
      </div>
    </div>
  );
};

export default BlackoutPaywallSplash;
